<template>
  <section class="grow divide-y divide-additional-50 divide-opacity-20 overflow-y-auto">
    <ul class="flex flex-col gap-y-2 px-9 py-6">
      <li>
        <MobileMenuLink :link="menuItem" class="py-1 text-2xl font-bold" @close="$emit('close')">
          {{ menuItem.title }}
        </MobileMenuLink>
      </li>
      <li v-for="item in mobileMainMenuItems" :key="item.title">
        <component
          :is="(item.id && customLinkComponents[item.id]) || LinkDefault"
          :item="item"
          @close="$emit('close')"
          @select-item="$emit('selectItem', item)"
        />
      </li>

      <template v-for="(item, index) in $cfg.navigationItems" :key="index">
        <li
          v-if="!item.productDropdown"
          :class="
            item.highlighted
              ? 'mr-4 w-fit cursor-pointer rounded-lg bg-[color:var(--color-sale-badge-bg)] px-3 py-1 font-semibold text-white hover:bg-[color:var(--color-secondary-500)]'
              : ''
          "
        >
          <a class="py-2 text-white text-2xl font-semibold" :href="item.url">
            {{ item.label }}
          </a>
        </li>
      </template>

      <template v-for="(item, index) in $cfg.topbarItems" :key="index">
        <li>
          <a class="py-2 text-white text-2xl font-semibold" :href="item.url" :target="item.newWindow ? '_blank' : ''">
            {{ item.label }}
          </a>
        </li>
      </template>
    </ul>

    <div class="flex flex-col gap-y-2 px-9 py-6">
      <template v-if="isAuthenticated">
        <!-- Account -->
        <div class="mb-4 mt-2 flex flex-row gap-4 text-xl">
          <div
            class="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-full ring-2 ring-accent-300"
          >
            <VcImage v-if="user.photoUrl" :src="user.photoUrl" :alt="user.contact?.fullName" class="size-12" lazy />
            <VcIcon v-else name="user" />
          </div>

          <div class="flex flex-col leading-tight">
            <div class="flex flex-wrap items-center gap-x-1 text-accent-100">
              <template v-if="operator">
                <span class="line-clamp-3 font-bold [word-break:break-word]">
                  {{ operator.contact?.fullName || operator.userName }}
                </span>

                <span class="text-accent-200">
                  {{ $t("shared.layout.header.top_header.logged_in_as") }}
                </span>
              </template>

              <span class="line-clamp-3 font-bold [word-break:break-word]">
                {{ user.contact?.fullName || user.userName }}
              </span>
            </div>

            <div>
              <button type="button" class="font-bold text-[--mobile-menu-navigation-color]" @click="signMeOut">
                {{ $t("shared.layout.header.link_logout") }}
              </button>
            </div>
          </div>
        </div>

        <div>
          <router-link
            v-if="loginOnBehalf"
            v-t="'shared.account.sign_in_form.impersonate.switch_user'"
            type="button"
            class="font-bold text-white"
            :to="{ name: 'SignInAdministrator' }"
            @click="$emit('close')"
          />
        </div>

        <!-- Account link -->
        <ul>
          <li>
            <MobileMenuLink
              v-if="mobileAccountMenuItem"
              :link="mobileAccountMenuItem"
              class="py-1 text-2xl font-bold"
              @select="$emit('selectItem', mobileAccountMenuItem!)"
            >
              {{ mobileAccountMenuItem.title }}
            </MobileMenuLink>
          </li>

          <!-- Corporate link -->
          <li>
            <MobileMenuLink
              v-if="mobileCorporateMenuItem && isCorporateMember"
              :link="mobileCorporateMenuItem"
              class="py-1 text-2xl font-bold"
              @select="$emit('selectItem', mobileCorporateMenuItem!)"
            >
              {{ mobileCorporateMenuItem.title }}
            </MobileMenuLink>
          </li>
        </ul>
      </template>

      <!-- Unauthorized links -->
      <ul v-else class="mb-1">
        <li>
          <MobileMenuLink
            v-for="item in unauthorizedMenuItems"
            :key="item.title"
            :link="item"
            class="py-1.5 text-2xl font-bold"
            @close="$emit('close')"
          >
            {{ item.title }}
          </MobileMenuLink>
        </li>
      </ul>

      <!-- Settings link -->
      <MobileMenuLink
        v-if="supportedCurrencies.length > 1"
        :link="settingsMenuItem"
        class="py-1 text-2xl font-bold"
        @select="$emit('selectItem', settingsMenuItem)"
      >
        {{ $t("shared.layout.header.mobile.settings") }}
      </MobileMenuLink>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useCurrency, useNavigations } from "@/core/composables";
import { useSignMeOut, useUser } from "@/shared/account";
import { useCustomMobileMenuLinkComponents } from "@/shared/layout/composables/useCustomMobileMenuLinkComponents";
import type { ExtendedMenuLinkType } from "@/core/types";
import LinkCart from "@/shared/layout/components/header/_internal/mobile-menu/link-components/link-cart.vue";
import LinkCompare from "@/shared/layout/components/header/_internal/mobile-menu/link-components/link-compare.vue";
import LinkDefault from "@/shared/layout/components/header/_internal/mobile-menu/link-components/link-default.vue";
import MobileMenuLink from "@/shared/layout/components/header/_internal/mobile-menu/mobile-menu-link.vue";
import { computedEager } from "@vueuse/core";
import { VirtoPermissions, CustomPermissions } from "@/core/enums";

interface IProps {
  menuItem: ExtendedMenuLinkType;
}

interface IEmits {
  (event: "close"): void;
  (event: "selectItem", item: ExtendedMenuLinkType): void;
}

defineEmits<IEmits>();
defineProps<IProps>();

const { signMeOut } = useSignMeOut();
const { user, operator, isAuthenticated, isCorporateMember, checkPermissions, isMultiOrganization } = useUser();
const { mobileMainMenuItems } = useNavigations();
const { t } = useI18n();
const { supportedCurrencies } = useCurrency();
const { registerCustomLinkComponent, customLinkComponents } = useCustomMobileMenuLinkComponents();

registerCustomLinkComponent({ id: "cart", component: LinkCart });
registerCustomLinkComponent({ id: "compare", component: LinkCompare });

const loginOnBehalf = computedEager<boolean>(() => checkPermissions(VirtoPermissions.LoginOnBehalf));
const showOrderHistory = computedEager<boolean>(() => checkPermissions(CustomPermissions.ShowOrderHistory));
const showInvoiceHistory = computedEager<boolean>(() => checkPermissions(CustomPermissions.ShowInvoiceHistory));
const showUserManagement = computedEager<boolean>(() => checkPermissions(CustomPermissions.ShowUserManagement));

const unauthorizedMenuItems: ExtendedMenuLinkType[] = [
  { route: { name: "SignIn" }, title: t("shared.layout.header.link_sign_in") },
  { route: { name: "SignUp" }, title: t("shared.layout.header.link_register_now") },
];

const settingsMenuItem: ExtendedMenuLinkType = {
  id: "settings",
  icon: "cog",
  children: [{}],
};

// Custom Innovadis
const mobileAccountMenuItem = computed<ExtendedMenuLinkType>(() => {
  return {
    id: "account",
    title: t("shared.layout.header.mobile.my_account"),
    icon: "user-circle",
    children: [
      {
        route: { name: "Profile" },
        title: t("shared.layout.header.mobile.account_menu.profile"),
        icon: "profile",
      },
      ...(isMultiOrganization.value
        ? [
            {
              route: { name: "DropOffPoint" },
              title: t("shared.account.navigation.links.drop_off_point"),
              icon: "truck",
            },
          ]
        : []),
      {
        route: { name: "ChangePasswordAccount" },
        title: t("shared.layout.header.mobile.account_menu.change_password"),
        icon: "pass",
      },
      ...(showOrderHistory.value
        ? [
            {
              route: { name: "Orders" },
              title: t("shared.layout.header.mobile.account_menu.orders"),
              icon: "clipboard-list",
            },
          ]
        : []),
      ...(showOrderHistory.value
        ? [
            {
              route: { name: "Backorders" },
              title: t("shared.layout.header.mobile.account_menu.backorders"),
              icon: "clipboard-list",
            },
          ]
        : []),
      ...(showInvoiceHistory.value
        ? [
            {
              route: { name: "Invoices" },
              title: t("shared.layout.header.mobile.account_menu.invoices"),
              icon: "clipboard-list",
            },
          ]
        : []),
      {
        route: { name: "Lists" },
        title: t("shared.layout.header.mobile.account_menu.lists"),
        icon: "list-v2",
      },
    ],
  };
});

// Custom Innovadis
const mobileCorporateMenuItem = computed<ExtendedMenuLinkType>(() => {
  return {
    id: "corporate",
    title: t("shared.layout.header.mobile.corporate"),
    icon: "case",
    children: [
      {
        route: { name: "CompanyInfo" },
        title: t("shared.layout.header.mobile.corporate_menu.company_info"),
        icon: "office-building",
      },
      ...(showUserManagement.value
        ? [
            {
              route: { name: "CompanyMembers" },
              title: t("shared.layout.header.mobile.corporate_menu.company_members"),
              icon: "user-group",
            },
          ]
        : []),
    ],
  };
});
</script>
